import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Key, Plus, Trash2, Copy, Check, X, Clock, 
  AlertCircle, Eye, EyeOff, ExternalLink, ArrowLeft
} from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';

const TokenManagement = ({ employeeId: propEmployeeId, employeeEmail: propEmail, readOnly: propReadOnly }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { success: notifySuccess, error: notifyError } = useNotification();
  
  // 从URL查询参数中获取employee_id
  const queryParams = new URLSearchParams(location.search);
  const urlEmployeeId = queryParams.get('employee_id');
  
  // 决定使用哪个employeeId：优先使用prop，其次使用URL参数
  const employeeId = propEmployeeId || (urlEmployeeId ? parseInt(urlEmployeeId) : null);
  
  // 决定readOnly模式：如果通过/tokens页面访问（有URL参数），则默认为管理员模式（非只读）
  // 否则使用传递的propReadOnly，默认为true（只读）
  const readOnly = propReadOnly !== undefined ? propReadOnly : (urlEmployeeId ? false : true);
  
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [newTokenDays, setNewTokenDays] = useState('365');
  const [generatedToken, setGeneratedToken] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [employeeEmail, setEmployeeEmail] = useState(propEmail || '');
  const [actionLoading, setActionLoading] = useState(false);
  const [showTokens, setShowTokens] = useState({}); // 控制每个token的显示状态

  const fetchTokens = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = employeeId 
        ? `/api/auth/list-tokens?employee_id=${employeeId}`
        : '/api/auth/list-tokens';
      const res = await fetch(url);
      const data = await res.json();
      if (data.status === 'success') {
        setTokens(data.data || []);
        if (data.employee_email) setEmployeeEmail(data.employee_email);
      } else {
        setError(data.message || 'Failed to load tokens');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  const handleGenerate = async () => {
    if (!newTokenName.trim()) return;
    setActionLoading(true);
    setGeneratedToken(null);
    try {
      const requestBody = {
        name: newTokenName.trim(),
        expires_in_days: parseInt(newTokenDays) || 365,
      };
      
      // 如果是管理员为其他员工生成token，添加employee_id参数
      if (!readOnly && employeeId) {
        requestBody.employee_id = employeeId;
      }
      
      const res = await fetch('/api/auth/generate-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });
      const data = await res.json();
      if (data.status === 'success') {
        setGeneratedToken(data.token);
        setNewTokenName('');
        setShowCreateForm(false);
        // Refresh list
        setTimeout(() => fetchTokens(), 500);
      } else {
        notifyError(data.message || 'Failed to generate token');
      }
    } catch (err) {
      notifyError('Error: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const toggleTokenVisibility = (tokenId) => {
    setShowTokens(prev => ({
      ...prev,
      [tokenId]: !prev[tokenId]
    }));
  };

  const handleRevoke = async (tokenId, tokenName) => {
    if (!window.confirm(`Revoke token "${tokenName}"? This action cannot be undone.`)) return;
    setActionLoading(true);
    try {
      const res = await fetch('/api/auth/revoke-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: tokenId }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        fetchTokens();
      } else {
        notifyError(data.message || 'Failed to revoke token');
      }
    } catch (err) {
      notifyError('Error: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyToken = (token, index) => {
    navigator.clipboard.writeText(token).then(() => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return dateStr.substring(0, 19).replace('T', ' ');
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg">
        <div className="flex items-center gap-2">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  // Filter out login session tokens, only show long-term tokens
  const longTermTokens = tokens.filter(token => 
    !token.name.toLowerCase().includes('login session')
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            {!readOnly && (
              <button
                onClick={() => navigate('/employees')}
                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                title={t('tokens.back_to_list')}
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <Key size={20} />
                {readOnly ? 'API Tokens' : t('tokens.manage_tokens')}
              </h3>
              {employeeEmail && (
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {employeeEmail}
                </p>
              )}
            </div>
          </div>
        </div>
        {!readOnly && (
          <button
            onClick={() => { setShowCreateForm(!showCreateForm); setGeneratedToken(null); }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={16} />
            {t('tokens.generate_token')}
          </button>
        )}
      </div>

      {/* Create form */}
      {showCreateForm && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Token Name
            </label>
            <input
              type="text"
              value={newTokenName}
              onChange={(e) => setNewTokenName(e.target.value)}
              placeholder="e.g., CI/CD Pipeline, AI Agent"
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg 
                bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Expires in (days)
            </label>
            <input
              type="number"
              value={newTokenDays}
              onChange={(e) => setNewTokenDays(e.target.value)}
              min="1"
              max="3650"
              className="w-32 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg 
                bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
            />
            <p className="text-xs text-slate-400 mt-1">Leave empty or 0 for no expiry</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleGenerate}
              disabled={actionLoading || !newTokenName.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {actionLoading ? 'Generating...' : 'Generate'}
            </button>
            <button
              onClick={() => setShowCreateForm(false)}
              className="px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 
                rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Newly generated token display */}
      {generatedToken && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 space-y-3">
          <div className="flex items-start gap-2">
            <AlertCircle size={18} className="text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-green-800 dark:text-green-300">
                Token generated successfully!
              </p>
              <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                Copy this token now. For security, it will not be shown again.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-white dark:bg-slate-800 border border-green-300 dark:border-green-700 rounded-lg p-3">
            <code className="flex-1 text-sm font-mono break-all text-slate-800 dark:text-slate-200">
              {generatedToken}
            </code>
            <button
              onClick={() => handleCopyToken(generatedToken, -1)}
              className="p-2 text-slate-500 hover:text-blue-600 transition-colors shrink-0"
              title="Copy token"
            >
              {copiedIndex === -1 ? <Check size={18} className="text-green-500" /> : <Copy size={18} />}
            </button>
          </div>
          <button
            onClick={() => setGeneratedToken(null)}
            className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Token list */}
      {longTermTokens.length === 0 ? (
        <div className="text-center py-12 text-slate-400 dark:text-slate-500">
          <Key size={40} className="mx-auto mb-3 opacity-40" />
          <p>No long-term API tokens yet</p>
          <p className="text-xs text-slate-400 mt-1">Login session tokens are not shown here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {longTermTokens.map((token, index) => {
            const expired = isExpired(token.expires_at);
            return (
              <div
                key={token.id}
                className={`bg-white dark:bg-slate-800 border rounded-lg p-4 
                  ${expired ? 'border-red-200 dark:border-red-800 opacity-60' : 'border-slate-200 dark:border-slate-700'}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-slate-900 dark:text-white truncate">
                        {token.name}
                      </h4>
                      {expired && (
                        <span className="px-2 py-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full">
                          Expired
                        </span>
                      )}
                    </div>
                    <div className="mt-2 space-y-3">
                      {/* Token display - 仅显示预览，原始token只在生成时可见一次 */}
                      <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-3">
                        <Key size={14} className="text-slate-400 shrink-0" />
                        <code className="flex-1 text-sm font-mono text-slate-500 dark:text-slate-400">
                          {token.token_preview}
                        </code>
                      </div>
                      
                      {/* Token metadata */}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500 dark:text-slate-400">
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          Created: {formatDate(token.created_at)}
                        </span>
                        {token.expires_at && (
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            Expires: {formatDate(token.expires_at)}
                          </span>
                        )}
                        {token.last_used_at && (
                          <span className="flex items-center gap-1">
                            Last used: {formatDate(token.last_used_at)}
                          </span>
                        )}
                        {!token.expires_at && (
                          <span className="text-green-600 dark:text-green-400">Never expires</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {!readOnly && (
                    <button
                      onClick={() => handleRevoke(token.id, token.name)}
                      disabled={actionLoading}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 
                        rounded-lg transition-colors disabled:opacity-50 shrink-0 ml-2"
                      title="Revoke token"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TokenManagement;
